import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeActionMediaViewSettings} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import type {RichTextContent} from '@wix/devzai-utils-react';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import type {DoppeHideableValue} from '../../client-server-common/types/doppe-hideable-value';
import {doppeHideableValueCreateHidden} from '../../client-server-common/types/doppe-hideable-value';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    DoppeActionCTAType
} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeDtoEvent} from '../../client-server-common/types/doppe-dto-event';
import type {
    BymoPagePropsContentSettingsMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';
import {
    DoppeExternalEventProvider,
    doppeExternalEventProviderResolveBusinessEntityReference
} from '../../client-server-common';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';
import type {CurrencyCode} from '@wix/devzai-utils-common';
import {arrayRemoveNullValues} from '@wix/devzai-utils-common';

export namespace DoppeActionTypeHoppEvent {

    export interface BaseActionSettings extends DoppeActionMediaViewSettings {
        eventPageLink: string;
        eventTitle: DoppeHideableValue<string>;
        description: DoppeHideableValue<RichTextContent>;
        eventImages: WixMediaResource[] | null;
        eventStartDate: DoppeHideableValue<string>;
        location: DoppeHideableValue<string>;
        priceData: DoppeHideableValue<DoppeActionPrice>;
        couponCode: DoppeHideableValue<string>;
        ribbon: DoppeHideableValue<string>;
        eventProvider: DoppeExternalEventProvider | null;
        eventProviderId: string | null,
    }

    export interface ActionSettings extends BaseActionSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
    }
}

export const doppeActionTypeHoppEventActionContentSettingsMetadata = {
    eventPageLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
    eventTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    eventImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    eventStartDate: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    location: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    priceData: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
    ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeHoppEvent.ActionSettings>

export const doppeActionTypeHoppEvent = doppeActionTypeDefineMetadata<DoppeActionTypeHoppEvent.ActionSettings>({
    id: 'io.bymo.action/hopp-event',
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveActionSettings: action => {
        const {...restSettings} = action.settings;
        return {
            eventPageLink: '',
            eventTitle: doppeHideableValueCreateHidden(''),
            description: doppeHideableValueCreateHidden(''),
            eventImages: null,
            eventStartDate: doppeHideableValueCreateHidden(''),
            location: doppeHideableValueCreateHidden(''),
            priceData: doppeHideableValueCreateHidden({
                actualPrice: 0,
                currencyCode: 'USD' as CurrencyCode
            }),
            couponCode: doppeHideableValueCreateHidden(''),
            ribbon: doppeHideableValueCreateHidden(''),
            eventProvider: DoppeExternalEventProvider.Hopp,
            eventProviderId: null,
            ...doppeActionGetMediaViewDefaultSettingsMigration(action.settings),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeHoppEventActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.OpenEvent]: true,
            }
        },
        supportsContentSections: {customContentSectionsTypes: {}}
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.eventProvider !== null && actionSettings.eventProviderId !== null ?
                doppeExternalEventProviderResolveBusinessEntityReference(
                    actionSettings.eventProvider,
                    actionSettings.eventProviderId
                ) : null
        ])
    }
});

export function convertDoppeDtoEventToHoppEventSettings(options: {
    event: DoppeDtoEvent,
    eventSettings?: DoppeActionTypeHoppEvent.ActionSettings
}): DoppeActionTypeHoppEvent.ActionSettings {
    const {event, eventSettings = {}} = options;
    const extractedPrice = extractPriceFromEvent(event);

    return {
        ...doppeActionContentSectionSettingsDefaultValuesMigration(eventSettings),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({ctaButtons: []}, false),
        ...eventSettings,
        ...doppeActionGetMediaViewDefaultSettingsMigration(eventSettings),
        eventPageLink: event.eventPageUrl ?? '',
        eventTitle: (event.name ?? ''),
        description: (event.description ?? ''),
        eventImages: event.media ?? [],
        eventStartDate: event.eventDates?.eventStartDate ?? '',
        location: event.location ?? '',
        priceData: extractedPrice,
        couponCode: doppeHideableValueCreateHidden(''),
        ribbon: doppeHideableValueCreateHidden(''),
        eventProviderId: event.id ?? null,
        eventProvider: DoppeExternalEventProvider.Hopp,
    } satisfies DoppeActionTypeHoppEvent.ActionSettings;
}

function extractPriceFromEvent(event: DoppeDtoEvent): DoppeActionPrice {
    const lowestPrice = event.lowestPrice ? parseFloat(event.lowestPrice.replace(/[^0-9.]/g, '')) : 0;
    const highestPrice = event.highestPrice ? parseFloat(event.highestPrice.replace(/[^0-9.]/g, '')) : 0;

    return {
        actualPrice: lowestPrice || highestPrice || 0,
        discountPrice: (lowestPrice && highestPrice && lowestPrice < highestPrice) ? highestPrice : undefined,
        currencyCode: 'USD' as CurrencyCode // Default currency, could be extracted from price format if needed
    };
}

